import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { config } from '@/config';
import { prepareHeaders } from '@/configuration/tokenHandling/prepareHeaders';
import { Criticality, VehicleWarning } from '@/features/warnings/utils/types';
import { buildWarningView } from '@/features/warnings/utils/warningCodeMessages';
import {
    SummaryRequest,
    VehicleWarningResponse,
    type VehicleWarnings,
    WarningFeedbackRequest,
    WarningFeedbackResponse,
    WarningSummary,
} from '@/generated/GeneratedApiTypes';

type VehicleWarningsParams = VehicleWarnings.GetVehicleWarnings.RequestParams &
    VehicleWarnings.GetVehicleWarnings.RequestQuery;

// The API response with each item enriched with the computed `messages`
type VehicleWarningsResponse = Omit<VehicleWarningResponse, 'items'> & {
    items: VehicleWarning[];
};

export const warningsApi = createApi({
    reducerPath: 'warningsApi',
    baseQuery: fetchBaseQuery({ baseUrl: config.backend.BACKEND_URI, prepareHeaders }),
    tagTypes: ['WarningFeedback'],
    endpoints: (build) => ({
        getVehicleWarnings: build.query<VehicleWarningsResponse, VehicleWarningsParams>({
            query: ({ assetId, limit, offset, criticality }) => {
                const params = new URLSearchParams();
                if (limit !== undefined) {
                    params.set('limit', String(limit));
                }
                if (offset !== undefined) {
                    params.set('offset', String(offset));
                }
                if (criticality && criticality.length > 0) {
                    criticality.forEach((c) => params.append('criticality', c));
                }
                const queryString = params.toString();
                return queryString ? `/vehicle-warnings/${assetId}?${queryString}` : `/vehicle-warnings/${assetId}`;
            },
            transformResponse: (response: VehicleWarningResponse): VehicleWarningsResponse => ({
                ...response,
                items: response.items.map((item) => ({
                    ...item,
                    criticality: item.criticality as Criticality,
                    messages: buildWarningView(item),
                })),
            }),
        }),
        getVehicleWarningSummary: build.query<WarningSummary, { assetId?: string }>({
            // POST is used because this endpoint is shared with the man-fleetstatus-aggregation-backend,
            // which needs to pass a list of asset IDs in the request body.
            query: ({ assetId }) => ({
                url: '/vehicle-warnings/summary',
                method: 'POST',
                body: (assetId ? { asset_ids: [assetId] } : {}) satisfies SummaryRequest,
            }),
        }),
        getWarningFeedback: build.query<WarningFeedbackResponse, { warningId: string }>({
            // A 404 means no feedback has been submitted yet for this warning. Callers should
            // check `error.status === 404` and treat it as a valid "no feedback yet" state
            // rather than a failure, since RTK Query otherwise reports this as `isError`.
            query: ({ warningId }) => `/vehicle-warnings/${warningId}/feedback`,
            providesTags: (_result, _error, { warningId }) => [{ type: 'WarningFeedback', id: warningId }],
        }),
        createWarningFeedback: build.mutation<
            WarningFeedbackResponse,
            { warningId: string; body: WarningFeedbackRequest }
        >({
            query: ({ warningId, body }) => ({
                url: `/vehicle-warnings/${warningId}/feedback`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_result, _error, { warningId }) => [{ type: 'WarningFeedback', id: warningId }],
        }),
        updateWarningFeedback: build.mutation<
            WarningFeedbackResponse,
            { warningId: string; body: WarningFeedbackRequest }
        >({
            query: ({ warningId, body }) => ({
                url: `/vehicle-warnings/${warningId}/feedback`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (_result, _error, { warningId }) => [{ type: 'WarningFeedback', id: warningId }],
        }),
        deleteWarningFeedback: build.mutation<void, { warningId: string }>({
            query: ({ warningId }) => ({
                url: `/vehicle-warnings/${warningId}/feedback`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { warningId }) => [{ type: 'WarningFeedback', id: warningId }],
        }),
    }),
});

export const {
    useGetVehicleWarningsQuery,
    useLazyGetVehicleWarningsQuery,
    useGetVehicleWarningSummaryQuery,
    useGetWarningFeedbackQuery,
    useCreateWarningFeedbackMutation,
    useUpdateWarningFeedbackMutation,
    useDeleteWarningFeedbackMutation,
} = warningsApi;
